// LeadHistoryDrawer: opens a dialog with the full activity feed for a lead
// plus an inline input to add a new note. Used from the Leads table.
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, MessageSquarePlus, User, ArrowRightLeft, CircleCheck, PenSquare, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { useStaffAuth } from '../context/StaffAuthContext';
import { apiErrorMessage } from '../lib/apiError';

const ICONS = {
  created: Sparkles,
  note: PenSquare,
  status_change: CircleCheck,
  reassignment: ArrowRightLeft,
};

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function LeadHistoryDrawer({ open, onOpenChange, lead }) {
  const { authHeader, API } = useStaffAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!lead?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/crm/leads/${lead.id}/activities`, { headers: authHeader() });
      setItems(res.data.activities || []);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load history'));
    } finally {
      setLoading(false);
    }
  }, [API, authHeader, lead]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const submitNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/crm/leads/${lead.id}/notes`, { body: note.trim() }, { headers: authHeader() });
      setItems((prev) => [...prev, res.data.activity]);
      setNote('');
      toast.success('Note added');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not add note'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] bg-[#F5F1E8] p-0 overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-[#0B3B2E]/10">
          <DialogTitle asChild>
            <h2 className="font-serif text-[22px] text-[#0B3B2E]">
              {lead?.name} · activity
            </h2>
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[#0E1F1A]/60">
            {lead?.email}{lead?.phone ? ` · ${lead.phone}` : ''}
          </DialogDescription>
        </div>

        <div className="max-h-[420px] overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="py-10 flex items-center justify-center text-[#0B3B2E]">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-center text-[13px] text-[#0E1F1A]/55 py-8">Nothing yet. Add the first note below.</p>
          ) : (
            <ol className="relative border-l border-[#0B3B2E]/12 pl-5 space-y-5">
              {items.map((a) => {
                const Icon = ICONS[a.type] || User;
                return (
                  <li key={a.id} className="relative">
                    <span className="absolute -left-[30px] top-0 w-6 h-6 rounded-full bg-[#0B3B2E] text-[#C8944B] flex items-center justify-center">
                      <Icon className="w-3 h-3" />
                    </span>
                    <div className="bg-white rounded-lg border border-[#0B3B2E]/10 px-3.5 py-2.5">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[11px] uppercase tracking-wider text-[#0B3B2E]/70 font-medium">
                          {a.type.replace('_', ' ')}
                        </p>
                        <p className="text-[11px] text-[#0E1F1A]/50">{formatWhen(a.created_at)}</p>
                      </div>
                      <p className="mt-1 text-[13px] text-[#0E1F1A]/85 whitespace-pre-wrap">{a.body}</p>
                      <p className="mt-1.5 text-[11px] text-[#0E1F1A]/50">
                        by {a.actor_name}{a.actor_kind && a.actor_kind !== 'system' ? ` · ${a.actor_kind}` : ''}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <form onSubmit={submitNote} className="px-6 py-4 border-t border-[#0B3B2E]/10 bg-white/50">
          <label className="text-[11px] uppercase tracking-wider text-[#0B3B2E]/70 font-medium">Add a note</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. Left voicemail, will retry Tue."
            className="mt-1.5 w-full rounded-md border border-[#0B3B2E]/20 bg-white p-2.5 text-[13px]" />
          <div className="mt-2 flex justify-end">
            <Button type="submit" disabled={busy || !note.trim()}
              className="rounded-full h-9 bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F] disabled:opacity-70">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MessageSquarePlus className="w-4 h-4 mr-1.5" /> Save note</>}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
